<script setup>
import { computed } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import { Badge } from '@/Components/ui/badge'
import { Video, MessageSquare, Clock, CheckCircle2 } from 'lucide-vue-next'

const props = defineProps({
  schedule: {
    type: Object,
    required: true,
  },
  lessonSpaceUrl: {
    type: String,
    default: null,
  },
  sessionStatus: {
    type: String,
    default: 'upcoming',
  },
  teacher: {
    type: Object,
    default: null,
  },
  students: {
    type: Array,
    default: () => [],
  },
  currentUser: {
    type: Object,
    required: true,
  },
  isTeacher: {
    type: Boolean,
    default: false,
  },
})

const sessionType = computed(() => {
  return props.schedule.availability_type === 'group-tutoring' ? 'Group Session' : '1:1 Session'
})

const formattedDate = computed(() => {
  if (!props.schedule.date) return ''
  const d = new Date(props.schedule.date + 'T' + (props.schedule.time || '00:00'))
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
})

const formattedTime = computed(() => {
  if (!props.schedule.time) return ''
  const [h, m] = props.schedule.time.split(':')
  const d = new Date()
  d.setHours(parseInt(h), parseInt(m))
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
})
</script>

<template>
  <DashboardLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ schedule.display_name }}</h1>
          <p class="text-sm text-gray-500 mt-1">
            {{ schedule.subject_name }} {{ schedule.level_name ? `• ${schedule.level_name}` : '' }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Badge
            v-if="sessionStatus === 'elapsed'"
            variant="outline"
            class="text-sm border-gray-300 text-gray-500"
          >
            Ended
          </Badge>
          <Badge variant="outline" class="text-sm">
            {{ sessionType }}
          </Badge>
        </div>
      </div>

      <!-- Video/Lesson Space -->
      <Card class="overflow-hidden">
        <!-- Session has ended -->
        <div v-if="sessionStatus === 'elapsed'" class="aspect-video bg-gray-50 flex items-center justify-center">
          <div class="text-center px-6 max-w-md">
            <CheckCircle2 class="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p class="text-xl font-semibold text-gray-700">This Session Has Ended</p>
            <p class="text-sm text-gray-500 mt-2">
              This session took place on {{ formattedDate }} at {{ formattedTime }}.
            </p>
            <div v-if="schedule.topic" class="mt-4 bg-white rounded-lg border border-gray-200 p-3 text-left">
              <p class="text-xs font-medium text-gray-400 uppercase tracking-wide">Topic Covered</p>
              <p class="text-sm text-gray-700 mt-1">{{ schedule.topic }}</p>
            </div>
          </div>
        </div>

        <!-- LessonSpace Iframe (joinable and link ready) -->
        <div v-else-if="lessonSpaceUrl" class="aspect-video">
          <iframe
            :src="lessonSpaceUrl"
            allow="camera; microphone; display-capture; fullscreen"
            allowfullscreen
            frameborder="0"
            class="w-full h-full"
          ></iframe>
        </div>

        <!-- Session upcoming — not yet joinable -->
        <div v-else-if="sessionStatus === 'upcoming'" class="aspect-video bg-gray-900 flex items-center justify-center">
          <div class="text-center text-white px-6 max-w-md">
            <Clock class="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p class="text-lg font-medium">Session Not Started Yet</p>
            <p class="text-sm text-gray-400 mt-2">
              This session is scheduled for {{ formattedDate }} at {{ formattedTime }}.
            </p>
            <p class="text-sm text-gray-500 mt-1">
              The lesson room will be available 15 minutes before the session starts.
            </p>
          </div>
        </div>

        <!-- Joinable but lesson space link not generated yet -->
        <div v-else class="aspect-video bg-gray-900 flex items-center justify-center">
          <div class="text-center text-white px-4">
            <Video class="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p class="text-lg font-medium">Setting Up Lesson Room...</p>
            <p class="text-sm text-gray-400 mt-2">
              The lesson room is being prepared. Please refresh in a moment.
            </p>
          </div>
        </div>
      </Card>

      <!-- Session Notes -->
      <Card>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <MessageSquare class="w-5 h-5" />
            Session Notes
          </CardTitle>
          <CardDescription>
            {{ isTeacher ? 'Notes for this session' : 'Notes from your teacher' }}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div v-if="schedule.note" class="bg-gray-50 rounded-lg p-4">
            <p class="text-gray-700">{{ schedule.note }}</p>
          </div>
          <div v-else class="text-center py-8 text-gray-500">
            <MessageSquare class="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No notes for this session yet</p>
          </div>
        </CardContent>
      </Card>
    </div>
  </DashboardLayout>
</template>
